// src/components/shared/BulkActionBar.js
// Barra flutuante (fixed bottom center) de ações em lote.
// Aparece SOMENTE quando count > 0, com transição slide-up suave.
//
// Props:
//   count     (number)  — quantos itens estão selecionados
//   onClear   (function)— callback para o "✕ Limpar" e auto-clear pós-sucesso
//   actions   (array)   — [{ label, icon?, color?, onClick, confirmMessage? }]
//                          color ∈ 'green'|'red'|'blue'|'gray' (default 'blue')
//                          confirmMessage pode ser string ou (count)=>string
//                          onClick recebe NADA — o consumidor já tem os ids via closure
//                          onClick deve ser async; rejeição mantém seleção
//   className (string)  — opcional, classes extras NO WRAPPER fixed (ex: override z-index
//                          quando usado dentro de modal com z muito alto). Default vazio.
//                          Para override de z-index, usar bang prefix do Tailwind:
//                          className="!z-[10002]" — sem isso, z-50 base vence por ordem
//                          de geração no CSS.
//
// Comportamento:
//   - Clique em ação → window.confirm(message) → se OK, dispara onClick
//   - Durante execução: spinner + todos os botões disabled
//   - Em sucesso (sem throw): chama onClear() automaticamente
//   - Em erro (throw): mantém seleção, libera UI (consumidor mostra toast)

import React, { useState } from 'react';
import { X, Loader2 } from 'lucide-react';

const COLOR_CLASSES = {
  green: 'bg-emerald-600 hover:bg-emerald-700 text-white',
  red:   'bg-red-600     hover:bg-red-700     text-white',
  blue:  'bg-blue-600    hover:bg-blue-700    text-white',
  gray:  'bg-slate-100   hover:bg-slate-200   text-slate-800 border border-slate-300',
};

function resolveConfirm(messageOrFn, count) {
  if (typeof messageOrFn === 'function') return messageOrFn(count);
  if (typeof messageOrFn === 'string') return messageOrFn;
  return null;
}

export default function BulkActionBar({ count = 0, onClear, actions = [], className = '' }) {
  const [busy, setBusy] = useState(false);

  const visible = count > 0;

  const runAction = async (action) => {
    if (busy) return;
    const msg = resolveConfirm(action.confirmMessage, count);
    if (msg && !window.confirm(msg)) return;

    setBusy(true);
    try {
      await action.onClick();
      if (typeof onClear === 'function') onClear();
    } catch (_err) {
      // consumidor é responsável pelo toast; barra apenas libera UI
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className={`fixed left-1/2 -translate-x-1/2 z-50 transition-all duration-200 ease-out ${
        visible
          ? 'bottom-4 opacity-100 pointer-events-auto'
          : 'bottom-0 opacity-0 pointer-events-none translate-y-4'
      } ${className}`}
      aria-hidden={!visible}
    >
      <div className="flex items-center gap-3 bg-slate-900 text-white rounded-lg shadow-2xl px-3 py-2 border border-slate-700">
        <div className="flex items-center gap-2 pr-2 border-r border-slate-700">
          {busy && <Loader2 size={14} className="animate-spin text-slate-300" />}
          <span className="text-xs font-medium">
            {count} {count === 1 ? 'selecionado' : 'selecionados'}
          </span>
        </div>

        <div className="flex items-center gap-2">
          {actions.map((action, idx) => {
            const Icon = action.icon;
            const color = COLOR_CLASSES[action.color] || COLOR_CLASSES.blue;
            return (
              <button
                key={action.label || idx}
                onClick={() => runAction(action)}
                disabled={busy}
                className={`px-3 py-1.5 rounded text-xs font-medium flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${color}`}
              >
                {Icon && <Icon size={13} />}
                {action.label}
              </button>
            );
          })}
        </div>

        <button
          onClick={onClear}
          disabled={busy}
          className="ml-1 p-1.5 rounded text-slate-300 hover:text-white hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed"
          title="Limpar seleção"
          aria-label="Limpar seleção"
        >
          <X size={14} />
        </button>
      </div>
    </div>
  );
}
